import React from 'react';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface BulkActionsSectionProps {
  onSelectAll: () => void;
  onClearAll: () => void;
  /** Number of options currently visible (after filtering) */
  visibleCount: number;
  /** Total number of options available (before filtering) */
  totalCount: number;
  /** Optional column description shown next to the count in a tooltip. */
  description?: string;
  /** Optional column field name used to produce a descriptive aria-label for the info tooltip trigger. */
  fieldName?: string;
}

export const BulkActionsSection: React.FC<BulkActionsSectionProps> = ({
  onSelectAll,
  onClearAll,
  visibleCount,
  totalCount,
  description,
  fieldName,
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-center gap-2">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onSelectAll}
            className="text-xs p-1.5 h-auto"
          >
            Select All Visible
          </Button>
          {description && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={
                    fieldName
                      ? `${fieldName} filter info`
                      : 'Filter options info'
                  }
                  className="inline-flex items-center justify-center rounded-sm p-0.5 text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Info className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent align="start" className="max-w-64">
                <p>{description}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="hover:bg-transparent hover:underline text-muted-foreground p-0 h-auto shrink-0"
        >
          Clear All
        </Button>
      </div>
      <span className="text-xs text-amber-500">
        {visibleCount} of {totalCount} unique values
      </span>
    </div>
  );
};
